import {
  Box,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  ExpandLess,
  ExpandMore,
} from "@mui/icons-material";
import {
  getComparator,
  Order,
  stableSort,
} from "./stewartTableHelper";
import React, {
  createRef,
  ReactElement,
  ReactNode,
  useEffect,
  useState,
} from "react";
import {
  borderRadius,
  borderSize,
  colors,
  defaultStyle,
  fontSize,
  gaps,
  iconSize,
  margin,
  padding,
  zIndex,
} from "theme/defaultStyle";
import CircularProgressWithLabel from "../circular-progress-with-label";
import LightTooltip from "../light-tooltip";
import StewartCheckbox from "../stewart-checkbox";
import StewartTableBodyEmpty, { EmptyStateType } from "./StewartTableBodyEmpty";
import StewartTableHeader from "./StewartTableHeader";
import useStewartTableStore from "utils/context/StewartTableContext";

export interface IRowIdentifier {
  disabled?: boolean;
  isEdit?: boolean;
  uniqueIdentifier: any;
  checked ?: boolean;
  actions?: string;
}

export interface SelectionConfig<T> {
  enabled?: boolean;
  renderRowDetails?: (row: T) => ReactNode;
  selectedRows?: string[];
  isRowSelected?: (row: T) => boolean;
  onToggleRow?: (row: T) => void;
  onToggleAllRows?: () => void;
  // Rows returning true get a disabled, unclickable checkbox.
  isRowDisabled?: (row: T) => boolean;
}

export interface DetailsViewConfig<T> {
  enabled?: boolean;
  renderRowDetails?: (row: T, collapse: () => void) => ReactNode;
  // Optional controlled expanded-row state — omit both to keep the previous
  // uncontrolled (internal-state) behavior.
  expandedRowIds?: string[];
  onExpandedRowIdsChange?: (ids: string[]) => void;
  // Hides the header's "Expand All / Collapse All" icon while leaving
  // per-row expand/collapse enabled.
  hideExpandAllButton?: boolean;
  // Rows returning false get no expand chevron (a spacer keeps the checkbox
  // aligned) and can never open a drawer, including via Expand All or a
  // controlled expandedRowIds entry. Omit for the previous behavior: every
  // row expandable.
  isRowExpandable?: (row: T) => boolean;
}

export interface StewartTableColumn<T extends IRowIdentifier> {
  field: keyof T;
  name: string;
  width?: number;
  minWidth?: number;
  sticky?: boolean;
  left?: number;
  right?: number;
  sortable?: boolean;
  resizable?: boolean;
  draggable?: boolean;
  position?: number;
  actionComponent?: (param?: T[]) => React.ReactNode;
  valueGetter?: (param: T) => React.ReactNode;
  classes?: string;
  classname?: string
}

type Props<T extends IRowIdentifier> = {
  cols: StewartTableColumn<T>[];
  rows: T[];
  page: number;
  noRowsMessage: string[];
  hiddenColumns?: (keyof T)[];
  rowsPerPage: number;
  loadingProgressPercent?: number;
  padding?: string | number;
  footerComponent?: ReactElement;
  showActionRow?: boolean;
  useDbPagingSorting?: boolean;
  detailsViewConfig?: DetailsViewConfig<T>;
  selectionConfig?: SelectionConfig<T>;
  order: Order;
  orderBy?: keyof T;
  emptyStateType?: EmptyStateType;
  onRequestSort?: (property: keyof T) => void;
  onColumnResize: (property: keyof T, newWidth: number) => void;
  onColumnsModified?: (columns: StewartTableColumn<T>[]) => void;
  rowHeaderWidth?: number;
};

type TableProps = {
  showActionRow?: boolean;
};

const CircularProgressContainer = styled("div")({
  alignItems: "center",
  display: "flex",
  justifyContent: "center",
  left: "50%",
  position: "fixed",
  top: "50%",
  transform: "translateX(-50%) translateY(-50%)",
  zIndex: zIndex.positiveMax,
  "& .MuiCircularProgress-circleDeterminate": {
    transition: "stroke-dashoffset 0ms",
  }
});

const StyledPaper = styled(Box)(({emptyStateType, hasExpandedRow}: {emptyStateType?: EmptyStateType; hasExpandedRow?: boolean}) => {
  const isEmptyState = emptyStateType === EmptyStateType.NO_FILTERS || emptyStateType === EmptyStateType.NO_RESULTS;
  // When a row's detail drawer is open, let the grid grow to fit it rather than
  // clamping to a fixed height (which would force the drawer to scroll inside).
  const autoHeight = isEmptyState || hasExpandedRow;

  return {
    padding: padding.zero,
    //maxHeight: "600px",
    minHeight: autoHeight ? "auto" : "600px",
    maxHeight: autoHeight ? "auto" : "calc(100vh - 600px)",  // 100 vh = (Full HD=1080/1024/1200/1280, 2K=1440, 4K=2160)
    "&.MuiTableContainer-root": {
      width: "calc(100% + 2px)",
      "& table:not(.details-table)": {
        width: "calc(100% - 2px)", 
      },
    },
  };
});

const StyledTable = styled(Table)(({ showActionRow }: TableProps) => ({
  borderCollapse: "separate",
  borderSpacing: 0,
  tableLayout: "fixed",
  position: "sticky",
  top: "0px",
  "& .MuiTableCell-root": {
    fontSize: fontSize.large,
    "&.row-header-cell": {
      backgroundColor: colors.white,
      left: 0,
      paddingBottom: padding.zero,
      paddingLeft: padding.large1,
      paddingRight: padding.zero,
      paddingTop: padding.zero,
      width: "55px",
      position: "sticky",
      zIndex: zIndex.positiveMin,
      "& .row-header-items": {
        alignItems: "center",
        display: "flex",
        flexDirection: "row",
        gap: gaps.small1,
        "& .MuiSvgIcon-root": {
          color: colors.blue15,
          cursor: "pointer",
        },
        // Stands in for the expand chevron on rows that can't be expanded, so
        // the checkbox stays in the same column across every row.
        "& .row-header-spacer": {
          display: "inline-block",
          flex: "none",
          height: iconSize.medium,
          width: iconSize.medium,
        },
        " & .MuiButtonBase-root": {
          padding: padding.small1_5,
        },
      },
    },
  },
  "& .MuiOutlinedInput-root": {
    height: "40px",
  },
  "& thead .MuiTableRow-root.header-actions": {
    height: "56px",
    "& th": {
      borderTop: defaultStyle.table.border,
      paddingRight: padding.small1_5,
      "&:first-child": {
        borderLeft: defaultStyle.table.border,
        borderTopLeftRadius: borderRadius.small,
        zIndex: zIndex.positiveMin,  
      },
      "&:last-child": {
        borderRight: defaultStyle.table.border,
        borderTopRightRadius: borderRadius.small,
      },
      "& div.disabled-input": {
        background: colors.grey10,
        borderTopLeftRadius: borderRadius.small,
        borderTopRightRadius: borderRadius.small,
      },
      "& button.Mui-disabled":{
        "& svg path": {
            stroke: colors.grey09,
        },
      }
    },
  },
  "& tbody .MuiTableRow-root": {
    "&:last-of-type": {
      "& td:first-of-type": {
        borderBottomLeftRadius: borderRadius.small,
      },
      "& td:last-of-type": {
        borderBottomRightRadius: borderRadius.small,
      },
    },
    "&.checked-row": {
      "& td": {
        backgroundColor: colors.blue13,
        borderTop: `1px solid ${colors.blue15}`,
        borderBottom: `1px solid ${colors.blue15}`,
        ":first-child": {
          borderLeft: `1px solid ${colors.blue15}`,
        },
        ":last-child": {
          borderRight: `1px solid ${colors.blue15}`,
          "& div.MuiBox-root": {
            // "marginLeft": "48px",
          }
        },
      },
    },
  },
  "& tbody.edit-mode > tr:not(.editing-row) td": {
    opacity: "unset",
    "& svg": {
      color: colors.blue11,
      "& path": {
        fill: colors.blue11,
      }
    },
    "& div": {
      color: `${colors.grey11} !important`,
    }
  },
  "& [data-pos='sticky']": {
    backgroundColor: colors.white,
    left: 0,
    position: "sticky",
    zIndex:  zIndex.positiveMin,
    "&.vertical-scroll": {
      zIndex: zIndex.zero,
      "&::-webkit-scrollbar": {
        zIndex: zIndex.zero,
      },
    },
  },
  ...(!showActionRow && {
    "& tbody .MuiTableRow-root:first-of-type": {
      "& td": {
        borderTop: defaultStyle.table.border,
      },
      "& td:first-of-type": {
        borderTopLeftRadius: borderRadius.small,
      },
      "& td:last-of-type": {
        borderTopRightRadius: borderRadius.small,
      },
    }
  }),
  "& tr.detailsViewContainer-row": {
    "& td.detailsViewContainer-cell": {
      borderBottom: defaultStyle.table.border,
      borderLeft: defaultStyle.table.border,
      borderRight: defaultStyle.table.border,
      borderTop: "none",
      margin: margin.zero,
      padding: padding.zero,
      width: "100%",
    },
  },
}));

const TableContainerWrapper = styled("div")({
  position: "relative",
});

const StyledTableHead = styled(TableHead)({
  userSelect: "none",
  position: "sticky",
  top: 0, 
  backgroundColor: colors.white,
  zIndex: zIndex.positiveMax,
  "& .header-labels": {
    height: "36px",
    "& th": {
      border: "none",
      paddingBottom: padding.zero,
      paddingLeft: padding.small1,
      paddingRight: padding.zero,
      paddingTop: padding.zero,   
    },
    "&.horizontal-scroll": {
      "&::-webkit-scrollbar": {
        zIndex: zIndex.positiveMin,
      },
    },
  },
});

const StyledTableRow = styled(TableRow)({
  backgroundColor: colors.white,
  borderWidth: `${borderSize.xsmall} ${borderSize.xsmall} 0 ${borderSize.xsmall}`,
  "& .MuiTableCell-root": {
    paddingBottom: padding.small1,
    paddingLeft: padding.medium1,
    paddingRight: padding.medium1,
    paddingTop: padding.small1,
    "& > *": {
      lineHeight: "150%",
    },
    "& .MuiButtonBase-root": {
      padding: padding.zero,
    },
  },
  "&:hover": {
    backgroundColor: colors.grey10,
  },
  "& td:first-of-type": {
    borderLeft: defaultStyle.table.border,
    backgroundColor: colors.white,
  },
  "& td:last-of-type": {
    borderRight: defaultStyle.table.border,
  }, "&.expanded > td": {
    borderBottom: "none",
  },
});

  const FooterContainer = styled(TableFooter)({
  width: "calc(100% + 2px)",
  lineHeight: "150%",
  fontSize: "16px",
  // paddingLeft: "25px",
  border: "none",
  display: "block",
  "& .MuiTableCell-root": {
    border: "none",
    fontSize: "16px",
  },

});

export default function StewartTable<T extends IRowIdentifier>({
  cols,
  rows,
  rowsPerPage,
  page,
  noRowsMessage,
  hiddenColumns,
  loadingProgressPercent = undefined,
  order,
  orderBy,
  footerComponent,
  showActionRow = true,
  useDbPagingSorting = false,
  detailsViewConfig,
  selectionConfig,
  emptyStateType,
  onRequestSort,
  onColumnResize,
  onColumnsModified,
  rowHeaderWidth,
}: Props<T>) {
  const [columns, setColumns] = useState<StewartTableColumn<T>[]>([]);
  const [editModeGrid, setEditModeGrid] = useState<boolean>(false);

  const [, { setFileData }] = useStewartTableStore();
  const messages = ["Loading results..."];
  const [internalExpandedRows, setInternalExpandedRows] = useState<string[]>([]);
  const isExpandedRowsControlled = detailsViewConfig?.expandedRowIds !== undefined;
  const expandedRows = isExpandedRowsControlled
    ? detailsViewConfig!.expandedRowIds!
    : internalExpandedRows;
  const setExpandedRows = (updater: string[] | ((prev: string[]) => string[])) => {
    const next = typeof updater === "function"
      ? (updater as (prev: string[]) => string[])(expandedRows)
      : updater;
    if (next === expandedRows) return;
    if (isExpandedRowsControlled) {
      detailsViewConfig?.onExpandedRowIdsChange?.(next);
    } else {
      setInternalExpandedRows(next);
    }
  };
  // Disambiguates rows that share a uniqueIdentifier (e.g. one
  // PolicyTransactionID fanned out into multiple rows by county) so
  // expanding one doesn't expand/collapse the other.
  const [expandedRowInstanceKeys, setExpandedRowInstanceKeys] = useState<string[]>([]);
  const getRowInstanceKey = (row: T) => {
    let occurrence = 0;
    for (const r of rows) {
      if (r === row) break;
      if (r.uniqueIdentifier === row.uniqueIdentifier) occurrence++;
    }
    return `${row.uniqueIdentifier}__${occurrence}`;
  };

  const tableRef = createRef<HTMLTableElement>();

  const handleRequestSort = (property: keyof T) => {
    if (onRequestSort) {
      onRequestSort(property);
    }
  };

  const determineSelectionState = (config :any) => {
    return config?.selectedRows?.length > 0 ? "Select" : "Unselect";
  };

  const handleColumnsModified = (sortedColumns: StewartTableColumn<T>[]) => {
    if (sortedColumns.length === cols.length) {
      setColumns(sortedColumns);
      onColumnsModified?.(sortedColumns);
    }
    else {
      const currentCols = new Set(sortedColumns.map((c) => c.field));
      const difference = cols.filter((c) => !currentCols.has(c.field));
      const acc = [...sortedColumns, ...difference];
      setColumns(acc);
      onColumnsModified?.(acc);
    }
  };

  const handleToggleSelectedAll = () => {
    selectionConfig?.onToggleAllRows?.();
  };

  const handleToggleSelected = (row: T) => {
    if (selectionConfig) {
      selectionConfig?.onToggleRow?.(row);
    }
  };

  // Defaults to true, so a config without isRowExpandable keeps every row
  // expandable exactly as before.
  const canExpandRow = (row: T) =>
    detailsViewConfig?.isRowExpandable?.(row) ?? true;

  const handleToggleShowDetailsAll = () => {
    const collapsing = expandedRows.length > 0;
    const updatedRows = collapsing
      ? []
      : rows.filter(canExpandRow).map(f => f.uniqueIdentifier);

    setExpandedRows(updatedRows);
    setExpandedRowInstanceKeys(
      collapsing
        ? []
        : paginatedRows.filter(canExpandRow).map((row) => getRowInstanceKey(row))
    );
  };

  const handleToggleShowDetails = (row: T) => {
    if (!canExpandRow(row)) return;
    const updatedRows = expandedRows.includes(row.uniqueIdentifier)
      ? expandedRows.filter(i => i !== row.uniqueIdentifier)
      : [...expandedRows, row.uniqueIdentifier];

    setExpandedRows(updatedRows);
    const key = getRowInstanceKey(row);
    setExpandedRowInstanceKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const isRowSelected = (row: T) => {
    return selectionConfig?.isRowSelected?.(row) === true;
  };

  const hasCompanyStatus = (obj: any): obj is { companyStatus: string } =>
  "companyStatus" in obj;

  const renderRowHeader = (row: T) => {
    if (!selectionConfig?.enabled && !detailsViewConfig?.enabled) {
      return null;
    } else {
      const isCheckboxDisabled =
        (hasCompanyStatus(row) && row.companyStatus === "Inactive") ||
        Boolean(selectionConfig?.isRowDisabled?.(row));
      const isThisInstanceExpanded =
        expandedRows.includes(row.uniqueIdentifier) &&
        expandedRowInstanceKeys.includes(getRowInstanceKey(row));

      return (
        <TableCell
          className="row-header-cell"
          key="row-header-cell"
        >
          <div className="row-header-items">
            {detailsViewConfig?.enabled &&
              (canExpandRow(row) ? (
                <LightTooltip
                  title={isThisInstanceExpanded ? "Collapse" : "Expand"}
                  arrow
                >
                  {isThisInstanceExpanded
                    ? <ExpandLess onClick={() => handleToggleShowDetails(row)} />
                    : <ExpandMore onClick={() => handleToggleShowDetails(row)} />}
                </LightTooltip>
              ) : (
                <span className="row-header-spacer" aria-hidden />
              ))
            }
            {selectionConfig?.enabled &&
              <LightTooltip
                title={isRowSelected(row) ? "Deselect" : "Select"}
                arrow
              >

                <StewartCheckbox
                  {...{ checked: isRowSelected(row) }}
                  onChange={() => handleToggleSelected(row)}
                  disabled={isCheckboxDisabled}
                />

              </LightTooltip>
            }
          </div>
        </TableCell >
      );
    }
  };

  const shouldRenderDetails = (row: T) => {
    // console.log("row:", row);
    return detailsViewConfig?.enabled
      && detailsViewConfig?.renderRowDetails
      // Row-scoped, so a stale or externally-supplied expandedRowIds entry
      // still can't open a drawer on a non-expandable row.
      && canExpandRow(row)
      && expandedRows.includes(row.uniqueIdentifier)
      && expandedRowInstanceKeys.includes(getRowInstanceKey(row));
  };

  const sortedRows = orderBy
    ? stableSort<T>(rows, getComparator(order, orderBy))
    : rows;

  const paginatedRows = useDbPagingSorting ? rows : sortedRows.slice(
    (page - 1) * rowsPerPage,
    (page - 1) * rowsPerPage + rowsPerPage
  );

  const visibleCols =
    hiddenColumns && hiddenColumns.length > 0
      ? columns.filter((c) => !hiddenColumns.includes(c.field))
      : columns;

  const totalColSpan = visibleCols.length
    + (selectionConfig?.enabled || detailsViewConfig?.enabled ? 1 : 0);

  useEffect(() => {
    const updatedColumns = cols.map(column => {
      return {
        ...column,
        hidden: !!hiddenColumns?.includes(column.field),
      };
    });
    setFileData(updatedColumns, rows, orderBy, order);
    const editModeGrid = rows.find((row: T) => row.isEdit);
    setEditModeGrid(!!editModeGrid)
  }, [columns, rows, order, orderBy]);

  useEffect(() => {
    setExpandedRows((prev) => {
      if (prev.length === 0) return prev;
      // Also drops rows that are still present but no longer expandable, e.g.
      // when an edit or a refresh changes the field the predicate reads.
      const visibleIds = new Set(
        rows.filter(canExpandRow).map((r) => r.uniqueIdentifier)
      );
      const next = prev.filter((id) => visibleIds.has(id));
      // Avoid setting state if nothing actually changed — prevents
      // unnecessary re-renders while drawer fields are being edited.
      return next.length === prev.length ? prev : next;
    });
  }, [rows]);

  useEffect(() => {
    setColumns(cols);
  }, [cols]);

  if (visibleCols.length === 0) return null;

  return (
    <TableContainerWrapper>
      <TableContainer component={StyledPaper} emptyStateType={emptyStateType} hasExpandedRow={expandedRows.length > 0}>
        <StyledTable
          id="stewartTable"
          ref={tableRef}
          key={[showActionRow].toString()} // NOTE: This is to work around a material ui 4 bug (fixed in MUI5) with style not always updating when props change.
          showActionRow={showActionRow}
        >
          <StyledTableHead>
            <StewartTableHeader
              cols={visibleCols}
              paginatedRows={paginatedRows}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              onColumnResize={onColumnResize}
              onColumnsModified={handleColumnsModified}
              showActionRow={showActionRow}
              useDetailsView={detailsViewConfig?.enabled && !detailsViewConfig?.hideExpandAllButton}
              detailsViewButtonState={expandedRows.length ? "collapse" : "expand"}
              selectionConfig={selectionConfig}
              onDetailsViewToggle={handleToggleShowDetailsAll}
              onSelectAllToggle={handleToggleSelectedAll}
              tableRef={tableRef}
              selectionState={determineSelectionState(selectionConfig)}
              rowHeaderWidth={rowHeaderWidth}
            />
          </StyledTableHead>
          {loadingProgressPercent !== undefined && rows.length === 0 ? (
            <>
              <StewartTableBodyEmpty
                messages={messages}
                colSpan={totalColSpan}
                emptyStateType={emptyStateType}
              />
              {loadingProgressPercent && (loadingProgressPercent > 0) && (
                <CircularProgressContainer>
                  <CircularProgressWithLabel value={loadingProgressPercent} />
                </CircularProgressContainer>
              )}
            </>
          ) : rows.length === 0 ? (
            <StewartTableBodyEmpty
              messages={noRowsMessage}
              colSpan={totalColSpan}
              emptyStateType={emptyStateType}
            />
          ) : (
            <>
              <TableBody className={editModeGrid ? "edit-mode" : ""}>
                {loadingProgressPercent &&
                  <CircularProgressContainer>
                    <CircularProgressWithLabel value={loadingProgressPercent} />
                  </CircularProgressContainer>
                }
                {paginatedRows.map((row) => (
                  // Keyed by row identity, not the bare id — two rows can
                  // share a uniqueIdentifier (see expandedRowInstanceKeys above).
                  <React.Fragment key={getRowInstanceKey(row)}>
                    <StyledTableRow
                      className={(expandedRows.includes(row.uniqueIdentifier) ? "expanded" : "") + (isRowSelected(row) ? "checked-row" : "")}
                    >
                      {/* {renderExpandCollapse(row)}
                    {renderSelected(row)} */}
                      {renderRowHeader(row)}
                      {visibleCols.map(
                        ({ valueGetter, field, sticky, left, right, classname }) => (
                          <TableCell
                            key={String(field)}
                            data-pos={sticky && "sticky"}
                            style={{
                              // Right-sticky cells must override the shared `left: 0`
                              // rule (else they'd pin left); force `left: auto`.
                              left: sticky && left !== undefined
                                ? left
                                : sticky && right !== undefined
                                ? "auto"
                                : undefined,
                              right: sticky && right !== undefined  ? right : undefined, }}
                            className={classname}                              
                            >
                            {(valueGetter ? valueGetter(row) : row[field]) as ReactNode}
                          </TableCell>
                        )
                      )}
                    </StyledTableRow>
                    {shouldRenderDetails(row) &&
                      <tr className="detailsViewContainer-row">
                        <td
                          colSpan={totalColSpan}
                          className="detailsViewContainer-cell"
                        >
                          {detailsViewConfig?.renderRowDetails?.(row, () => {
                            setExpandedRows((prev) =>
                              prev.filter((i) => i !== row.uniqueIdentifier)
                            );
                            const key = getRowInstanceKey(row);
                            setExpandedRowInstanceKeys((prev) =>
                              prev.filter((k) => k !== key)
                            );
                          })}
                        </td>
                      </tr>
                    }
                  </React.Fragment>
                ))}
              </TableBody>

            </>
          )}

        </StyledTable>
      </TableContainer>
      {footerComponent && <FooterContainer>{footerComponent}</FooterContainer>}
    </TableContainerWrapper>

  );

}
